import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Image,
  Input,
  Select,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { Slider, Tooltip, Switch } from "antd"
import axios from "axios"
import { Field, Form, Formik } from "formik"
import React, { useEffect, useMemo, useState } from "react"
import { FiUpload } from "react-icons/fi"
import { RiDeleteBin6Line } from "react-icons/ri"
import { useDispatch } from "react-redux"
import * as Yup from "yup"
import { useRestaurantMaster } from "../../store/restaurant/reducer"
import {
  REQUEST_CREATE_MASTER_RESTAURANT,
  REQUEST_MASTER_RESTAURANT,
  REQUEST_UPDATE_MASTER_RESTAURANT,
} from "../../store/restaurant/restaurantActionTypes"
import FormEditor from "../form/formField/FormEditor"
import DishesEditModal from "../modals/DishesEditModal"

interface RestaurantFormDrawerProps {
  isOpen: boolean
  onClose: () => void
  restaurantData?: any
}

interface Option {
  _id: string
  name: string
  [key: string]: any
}

interface ImageState {
  files: File[]
  previews: string[]
}
interface FormValues {
  name: string
  street: string
  city: string
  state: string
  zip: string
  about: string
  phone: string
  email: string
  cuisines: string[]
  crowd: string[]
  features: string[]
  priceBand: string
  serveTime: string[]
  bookingFeeLunch: string
  bookingFeeDinner: string
  openingTime: string
  closingTime: string
  longitude: string
  latitude: string
  gallery: string[]
  menu: string[]
  mainImage: string
  rating: number
  popularDishes: string[]
  doesIncludeDrinks: boolean
  isSponsored: boolean
  termsAndCondition: string
}

const SERVE_TIME_OPTIONS = ["Lunch", "Dinner"]
const PRICE_BAND_OPTIONS = ["£", "££", "£££"]
const MultiSelectWithTags = ({
  label,
  options,
  selectedValues,
  onChange,
  name,
  errors,
  touched,
  apiOptions = [],
  onAddNew,
  onEdit,
  onDelete,
}: any) => {
  const handleSelectChange = (e: any) => {
    const selectedOption =
      apiOptions.find((option: any) => option.name === e.target.value) ||
      (options.includes(e.target.value) ? e.target.value : null)

    if (selectedOption) {
      const valueToAdd =
        typeof selectedOption === "object" ? selectedOption._id : selectedOption

      if (!selectedValues.includes(valueToAdd)) {
        onChange([...selectedValues, valueToAdd])
      }
    }
  }

  const removeTag = (tagToRemove: any) => {
    onChange(selectedValues.filter((tag: any) => tag !== tagToRemove))
  }
  const displayOptions =
    apiOptions.length > 0
      ? apiOptions.map((option: any) => option.name)
      : options
  return (
    <FormControl isInvalid={errors && touched}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        marginBottom={"20px"}
      >
        <FormLabel>{label}</FormLabel>
      </Flex>
      <Select
        value=""
        onChange={handleSelectChange}
        placeholder="Select"
        mb={2}
      >
        {displayOptions?.map((option: any) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
      <Flex flexWrap="wrap" gap={2}>
        {selectedValues.map((value: any) => {
          const selectedOptionName =
            apiOptions?.find((option: any) => option._id === value)?.name ||
            value

          return (
            <Box
              key={value}
              bg="#FFF1F1"
              color="#E24441"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="sm"
              display="inline-flex"
              alignItems="center"
            >
              {selectedOptionName}
              <Box
                as="button"
                ml={2}
                onClick={() => removeTag(value)}
                color="#E24441"
                fontSize="lg"
                lineHeight="1"
                _hover={{ color: "red.600" }}
              >
                ×
              </Box>
            </Box>
          )
        })}
      </Flex>
      <FormErrorMessage>{errors}</FormErrorMessage>
    </FormControl>
  )
}

const RestaurantEditDrawer: React.FC<RestaurantFormDrawerProps> = ({
  isOpen,
  onClose,
  restaurantData,
}) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Restaurant name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters"),
    about: Yup.string()
      .required("About description is required")
      .min(20, "Description must be at least 20 characters")
      .max(1000, "Description must not exceed 1000 characters"),
    street: Yup.string()
      .required("Street address is required")
      .min(5, "Street address must be at least 5 characters"),
    city: Yup.string()
      .required("City is required")
      .min(2, "City name must be at least 2 characters"),
    state: Yup.string()
      .required("State is required")
      .min(2, "State name must be at least 2 characters"),
    zip: Yup.string()
      .required("Post code is required")
      .matches(/^[a-zA-Z0-9]+$/, "Post code must be alphanumeric")
      .min(1, "Post code must be a minimum of 1 character")
      .max(8, "Post code must be a maximum of 8 characters"),

    longitude: Yup.number()
      .required("Longitude is required")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
    latitude: Yup.number()
      .required("Latitude is required")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    phone: Yup.string()
      .required("Phone number is required")
      .max(13, "Phone number should be less than or equal to 13 digits")
      .min(1, "Phone number should be greater than or equal to 1 digits"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    bookingFeeLunch: Yup.number()
      .required("Lunch booking fee is required")
      .min(0, "Fee cannot be negative"),
    bookingFeeDinner: Yup.number()
      .required("Dinner booking fee is required")
      .min(0, "Fee cannot be negative"),
    priceBand: Yup.string()
      .required("Price band is required")
      .oneOf(["£", "££", "£££"], "Invalid price band"),
    serveTime: Yup.array()
      .min(1, "At least one serve time must be selected")
      .required("Serve time is required"),
    cuisines: Yup.array()
      .min(1, "At least one cuisine type must be selected")
      .required("Cuisines are required"),
    crowd: Yup.array()
      .min(1, "At least one crowd type must be selected")
      .required("Crowd types are required"),
    features: Yup.array()
      .min(1, "At least one feature must be selected")
      .required("Features are required"),

    mainImage: Yup.string()
      .required("Main image is required")
      .test("mainImage", "Main image is required", function (value) {
        return !!value || !!mainImage.imageId
      }),
    gallery: Yup.array().test(
      "galleryLength",
      "Minimum 1 gallery images required",
      function () {
        return galleryImages.previews.length >= 1
      }
    ),
    menu: Yup.array().test(
      "menuLength",
      "Minimum 1 menu images required",
      function () {
        return menuImages.previews.length >= 1
      }
    ),
    rating: Yup.number()
      .min(1, "Rating must be at least 1")
      .max(5, "Rating must be at most 5")
      .required("Rating is required"),
  })

  const customSlideStyles = {
    trackStyle: {
      backgroundColor: "#E24441",
    },
    railStyle: {
      backgroundColor: "#FFF1F1",
    },
    handleStyle: {
      borderColor: "#E24441",
      boxShadow: "none",
    },
    mark: {
      fill: "#E24441",
    },
    activeDotStyle: {
      backgroundColor: "transparent",
      border: "none",
    },
    inactiveDotStyle: {
      backgroundColor: "transparent",
    },
  }
  const [isAddDishModalOpen, setIsAddDishModalOpen] = useState(false)
  const [selectedDish, setSelectedDish] = useState<any>(null)
  const { masterRestaurant, busy, message } = useRestaurantMaster()
  console.log("restaurantData", restaurantData)

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()

  const handleDeleteDish = (dish: any) => {
    console.log("Edit dish:", dish)
    setSelectedDish(dish)
    onDeleteOpen()
  }
  const handleAddNewDish = () => {
    setIsAddDishModalOpen(true)
  }
  const handleEditDish = (dish: any) => {
    setSelectedDish(dish)
    setIsAddDishModalOpen(true)
  }

  const handleCloseDishModal = () => {
    setIsAddDishModalOpen(false)
    setSelectedDish(null)
  }

  const handleSaveDish = async (newDishData: any) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/dishes-category`,
        newDishData
      )

      const dishesRes = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/dishes-category`
      )
      setDishesOptions(dishesRes.data.data)

      setIsAddDishModalOpen(false)

      toast({
        title: "Dish Added",
        description: "New dish category added successfully",
        status: "success",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add new dish",
        status: "error",
      })
    }
  }
  const handleDishAdded = async () => {
    try {
      const dishesRes = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/dishes-category`
      )
      setDishesOptions(dishesRes.data.data)
      setSelectedDish(null)
      setIsAddDishModalOpen(false)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh dishes",
        status: "error",
      })
    }
  }
  const dispatch = useDispatch()
  const toast = useToast()
  const [termsTemplates, setTermsTemplates] = useState<any[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [markdownContent, setMarkdownContent] = useState("")
  const [originalMarkdownContent, setOriginalMarkdownContent] = useState("")
  const [galleryImages, setGalleryImages] = useState<ImageState>({
    files: [],
    previews: [],
  })
  const [menuImages, setMenuImages] = useState<ImageState>({
    files: [],
    previews: [],
  })
  const [uploadingImages, setUploadingImages] = useState(false)
  const [galleryImageIds, setGalleryImageIds] = useState<string[]>([])
  const [menuImageIds, setMenuImageIds] = useState<string[]>([])

  const [mainImage, setMainImage] = useState({
    file: null,
    preview: restaurantData?.image?.url
      ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${restaurantData.image.url}`
      : "",
    imageId: restaurantData?.image?._id || "",
  })
  const [goodForOptions, setGoodForOptions] = useState([])

  const [cuisineOptions, setCuisineOptions] = useState<Option[]>([])
  const [featureOptions, setFeatureOptions] = useState([])
  const [dishesOptions, setDishesOptions] = useState([])

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const goodForRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/good-for`
        )
        console.log("goodForRes", goodForRes.data.data)
        setGoodForOptions(goodForRes.data.data)

        const cuisineRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/cuisine`
        )
        console.log("cuisineRes", cuisineRes.data.data)
        setCuisineOptions(cuisineRes.data.data)

        const featureRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/feature`
        )
        console.log("featureRes", featureRes.data.data)
        setFeatureOptions(featureRes.data.data)

        const dishesRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/dishes-category`
        )
        console.log("dishesRes", dishesRes.data.data)
        setDishesOptions(dishesRes.data.data)
      } catch (error) {
        console.error("Error fetching options:", error)
      }
    }

    fetchOptions()
  }, [])

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const response = await axios.get(
  //         `${process.env.REACT_APP_BASE_URL}/admin/tnc`
  //       );
  //       console.log("response is", response.data.data);

  //       setTermsTemplates(response.data.data);

  //       if (response.data.data.length > 0) {
  //         const firstTemplate = response.data.data[0];
  //         setSelectedTemplate(firstTemplate);
  //         setMarkdownContent(firstTemplate.markdown);
  //         setOriginalMarkdownContent(firstTemplate.markdown);
  //       }
  //       setLoading(false);
  //     } catch (err) {
  //       console.log("error is", err);
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/tnc`
        )
        setTermsTemplates(response.data.data)

        console.log({ tnc: response.data.data })

        // If there's no restaurantData, return
        if (!restaurantData) {
          setLoading(false)
          return
        }

        // Check if the restaurant data has a termsAndCondition value
        if (restaurantData?.termsAndCondition) {
          setSelectedTemplate(null) // Clear selected template
          setMarkdownContent(restaurantData.termsAndCondition) // Set custom terms
          setOriginalMarkdownContent(restaurantData.termsAndCondition)
        } else {
          // Set the first template from fetched data
          if (response.data.data.length > 0) {
            const firstTemplate = response.data.data[0]
            setSelectedTemplate(firstTemplate)
            setMarkdownContent(firstTemplate.markdown)
            setOriginalMarkdownContent(firstTemplate.markdown)
          }
        }
        setLoading(false)
      } catch (err) {
        console.log("error is", err)
        setLoading(false)
      }
    }

    fetchData()
  }, [restaurantData])

  const galleryImageShow = async () => {
    if (!restaurantData) return
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/gallery/${restaurantData._id}`
      )

      let images = []
      if (Array.isArray(response.data.data)) {
        images = response.data.data?.[0]?.images || []
      } else {
        images = response.data.data?.images || []
      }

      const previews = images.map((img: any) =>
        img?.url
          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.url}`
          : img.url
      )
      const ids = images.map((img: any) => img._id)

      console.log("images of galleryyy", images)
      setGalleryImages({ files: [], previews })
      setGalleryImageIds(ids)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load gallery images",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const menuImageShow = async () => {
    if (!restaurantData) return
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/menu/${restaurantData._id}`
      )
      let images = []
      if (Array.isArray(response.data.data)) {
        images = response.data.data?.[0]?.images || []
      } else {
        images = response.data.data?.images || []
      }

      const previews = images.map((img: any) =>
        img?.url
          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.url}`
          : img.url
      )
      const ids = images.map((img: any) => img._id)
      console.log("images of menuuuuuu", images)
      setMenuImages({ files: [], previews })
      setMenuImageIds(ids)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load menu images",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const uploadMainImage = async (file: File) => {
    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/upload/media`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      console.log("Upload response:", response.data)
      console.log("Image ID from upload:", response.data.data._id)
      return response.data.data._id
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload the main image",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      throw error
    }
  }

  const handleMainImageUpload = async (
    event: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files[0]
    if (!file) return
    setUploadingImages(true)

    try {
      const preview = URL.createObjectURL(file)
      const imageId = await uploadMainImage(file)

      setMainImage({ file, preview, imageId })
      setFieldValue("mainImage", imageId)

      toast({
        title: "Success",
        description: "Main image uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload the main image",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setUploadingImages(false)
    }
  }

  const removeMainImage = (
    setFieldValue: (field: string, value: any) => void
  ) => {
    setMainImage({ file: null, preview: "", imageId: "" })
    setFieldValue("mainImage", "")

    const fileInput = document.getElementById(
      "main-image-upload"
    ) as HTMLInputElement
    if (fileInput) fileInput.value = ""

    toast({
      title: "Main Image Removed",
      description: "The main image has been removed.",
      status: "info",
      duration: 3000,
      isClosable: true,
    })
  }

  const uploadImage = async (file: File) => {
    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/upload/media`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      return response.data.data._id
    } catch (error) {
      throw new Error("Failed to upload image")
    }
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "gallery" | "menu",
    setFieldValue: (field: string, value: any) => void
  ) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    setUploadingImages(true)

    try {
      const previews = files.map((file) => URL.createObjectURL(file))
      let updatedFiles, updatedPreviews, updatedImageIds

      if (type === "gallery") {
        updatedFiles = [...galleryImages.files, ...files]
        updatedPreviews = [...galleryImages.previews, ...previews]
        setGalleryImages({
          files: updatedFiles,
          previews: updatedPreviews,
        })
      } else {
        updatedFiles = [...menuImages.files, ...files]
        updatedPreviews = [...menuImages.previews, ...previews]
        setMenuImages({
          files: updatedFiles,
          previews: updatedPreviews,
        })
      }

      const uploadPromises = files.map((file) => uploadImage(file))
      const newImageIds = await Promise.all(uploadPromises)

      if (type === "gallery") {
        updatedImageIds = [...galleryImageIds, ...newImageIds]
        setGalleryImageIds(updatedImageIds)
        setFieldValue("gallery", updatedImageIds)
      } else {
        updatedImageIds = [...menuImageIds, ...newImageIds]
        setMenuImageIds(updatedImageIds)
        setFieldValue("menu", updatedImageIds)
      }

      toast({
        title: "Success",
        description: "Images uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      })

      event.target.value = ""
    } catch (error) {
      console.error("Upload error:", error)
      toast({
        title: "Error",
        description: "Failed to upload images",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setUploadingImages(false)
    }
  }

  const removeImage = (
    index: number,
    type: "gallery" | "menu",
    setFieldValue: (field: string, value: any) => void
  ) => {
    const imageState = type === "gallery" ? galleryImages : menuImages
    const setImageState = type === "gallery" ? setGalleryImages : setMenuImages
    const imageIds = type === "gallery" ? galleryImageIds : menuImageIds
    const setImageIds =
      type === "gallery" ? setGalleryImageIds : setMenuImageIds

    const newPreviews = [...imageState.previews]
    newPreviews.splice(index, 1)

    const newFiles = [...imageState.files]
    newFiles.splice(index, 1)

    const newImageIds = [...imageIds]
    newImageIds.splice(index, 1)

    setImageState({
      files: newFiles,
      previews: newPreviews,
    })

    setImageIds(newImageIds)
    setFieldValue(type, newImageIds)

    toast({
      title: "Image Removed",
      description: `The image has been removed from the ${type} section.`,
      status: "info",
      duration: 3000,
      isClosable: true,
    })
  }

  useEffect(() => {
    if (isOpen && restaurantData) {
      setMainImage({
        file: null,
        preview: restaurantData?.image?.url
          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${restaurantData.image.url}`
          : "",
        imageId: restaurantData?.image?._id || "",
      })
      galleryImageShow()
      menuImageShow()
    }
  }, [isOpen, restaurantData])

  const initialValues = useMemo(() => {
    if (restaurantData) {
      return {
        name: restaurantData?.name || "",
        street: restaurantData?.address?.street || "",
        city: restaurantData?.address?.city || "",
        state: restaurantData?.address?.state || "",
        zip: restaurantData?.address?.zip || "",
        about: restaurantData?.about || "",
        phone: restaurantData?.contact?.phone || "",
        email: restaurantData?.contact?.email || "",
        cuisines: restaurantData?.cuisines || [],
        crowd: restaurantData?.crowd || [],
        features: restaurantData?.features || [],

        popularDishes:
          restaurantData?.popularDishes?.map((dish: any) => dish._id) || [],
        priceBand: restaurantData?.priceBand || "$",
        serveTime: restaurantData?.serveTime || [],
        bookingFeeLunch: restaurantData?.bookingFeePerPerson?.lunch || "",
        bookingFeeDinner: restaurantData?.bookingFeePerPerson?.dinner || "",
        openingTime: restaurantData?.openingTime || "12:00",
        closingTime: restaurantData?.closingTime || "23:00",
        longitude: restaurantData?.locationPoint?.coordinates[0] || "",
        latitude: restaurantData?.locationPoint?.coordinates[1] || "",
        mainImage: restaurantData?.image?._id || "",

        gallery: restaurantData?.images || [],
        menu: restaurantData?.menu || [],
        rating: restaurantData?.rating || 3,

        doesIncludeDrinks: restaurantData?.doesIncludeDrinks || false,
        isSponsored: restaurantData?.isSponsored || false,
        termsAndCondition: restaurantData?.termsAndCondition || "",
      }
    }
    return {
      name: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      about: "",
      phone: "",
      email: "",
      cuisines: [],
      crowd: [],
      features: [],
      popularDishes: [],
      priceBand: "$",
      serveTime: [],
      bookingFeeLunch: "",
      bookingFeeDinner: "",
      openingTime: "12:00",
      closingTime: "23:00",
      longitude: "",
      latitude: "",
      mainImage: "",
      gallery: [],
      menu: [],
      rating: 3,
      doesIncludeDrinks: false,
      isSponsored: false,
      termsAndCondition: "",
    }
  }, [restaurantData])

  const gallleryImageSubmit = async (restaurantId: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/gallery/${restaurantId}`,
        {
          images: galleryImageIds,
        }
      )
      console.log("Gallery images submitted:", response)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit gallery images",
        status: "error",
      })
    }
  }

  const menuImageSubmit = async (restaurantId: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/menu/${restaurantId}`,
        {
          images: menuImageIds,
        }
      )
      console.log("Menu images submitted:", response)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit menu images",
        status: "error",
      })
    }
  }

  const handleSubmit = (
    values: FormValues,
    { setSubmitting, setErrors, setFieldError }: any
  ) => {
    console.log("Form values:", values)
    console.log("mainiiiiiiiiiiii", mainImage)
    const validationErrors: any = {}

    if (galleryImages.previews.length < 1) {
      validationErrors.gallery = "Minimum 1 gallery images required"
    }

    if (menuImages.previews.length < 1) {
      validationErrors.menu = "Minimum 1 menu images required"
    }

    if (!mainImage.imageId) {
      validationErrors.mainImage = "Main image is required"
    }
    if (Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach((key) => {
        setFieldError(key, validationErrors[key])
      })

      toast({
        title: "Validation Error",
        description: "Please check image requirements",
        status: "error",
        duration: 5000,
        isClosable: true,
      })

      setSubmitting(false)
      return
    }

    try {
      const payload = {
        name: values.name,
        locationPoint: {
          type: "Point",
          coordinates: [Number(values.longitude), Number(values.latitude)],
        },
        address: {
          street: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
        },
        about: values.about,
        contact: {
          phone: values.phone,
          email: values.email,
        },

        cuisines: values.cuisines.map((id) => {
          const found: Option | undefined = cuisineOptions.find(
            (option: Option) => option._id === id
          )
          return found ? (found as Option).name : id
        }),
        crowd: values.crowd.map((id) => {
          const found: Option | undefined = goodForOptions.find(
            (option: Option) => option._id === id
          )
          return found ? (found as Option).name : id
        }),
        features: values.features.map((id) => {
          const found: Option | undefined = featureOptions.find(
            (option: Option) => option._id === id
          )
          return found ? (found as Option).name : id
        }),
        popularDishes: values.popularDishes,
        priceBand: values.priceBand,
        serveTime: values.serveTime,
        bookingFeePerPerson: {
          lunch: Number(values.bookingFeeLunch),
          dinner: Number(values.bookingFeeDinner),
        },
        openingTime: values.openingTime,
        closingTime: values.closingTime,

        image: mainImage.imageId,
        rating: values.rating,
        doesIncludeDrinks: values.doesIncludeDrinks,
        isSponsored: values.isSponsored,
        termsAndCondition: values.termsAndCondition,
      }

      if (restaurantData?._id) {
        const data = {
          payload,
          _id: restaurantData._id,
        }
        dispatch({
          type: REQUEST_UPDATE_MASTER_RESTAURANT,
          data,
          onSuccess: async (response: any) => {
            try {
              await gallleryImageSubmit(restaurantData._id)
              await menuImageSubmit(restaurantData._id)

              dispatch({ type: REQUEST_MASTER_RESTAURANT })
              toast({
                title: "Success",
                description: "Restaurant updated successfully",
                status: "success",
              })
              onClose()
            } catch (error) {
              toast({
                title: "Error",
                description: "Failed to upload images after restaurant update",
                status: "error",
              })
            }
          },
        })

        toast({
          title: "Success",
          description: "Restaurant updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } else {
        dispatch({
          type: REQUEST_CREATE_MASTER_RESTAURANT,
          payload,
          onSuccess: async (response: any) => {
            try {
              const newRestaurantId = response.data.data._id

              await gallleryImageSubmit(newRestaurantId)
              await menuImageSubmit(newRestaurantId)

              dispatch({ type: REQUEST_MASTER_RESTAURANT })
              toast({
                title: "Success",
                description: "Restaurant created successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
              })
              onClose()
            } catch (error) {
              toast({
                title: "Error",
                description:
                  "Failed to upload images after restaurant creation",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
              })
            }
          },
        })

        toast({
          title: "Success",
          description: "Restaurant created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      }

      setSubmitting(false)
      onClose()
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving restaurant data",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  }

  const previousRestaurants = useMemo(() => {
    return masterRestaurant
  }, [])

  useEffect(() => {
    if (restaurantData) {
      const restaurantFromStore = masterRestaurant.find(
        (r: any) => r._id === restaurantData._id
      )

      if (restaurantFromStore !== restaurantData) {
        toast({
          title: "Success",
          description: "Restaurant has been updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        })
        onClose()
      }
    } else {
      if (masterRestaurant.length !== previousRestaurants.length) {
        toast({
          title: "Success",
          description: "New restaurant has been created",
          status: "success",
          isClosable: true,
          position: "top-right",
        })
        onClose()
      }
    }
  }, [masterRestaurant, previousRestaurants, restaurantData, onClose, toast])

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => !uploadingImages && onClose()}
      size="lg"
    >
      <DrawerOverlay />
      <DrawerContent>
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, setFieldValue, errors, touched, values }) => (
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DrawerHeader
                borderBottom="1px solid #E0E0E0"
                background="#e244411a"
                alignItems={"stretch"}
                flex="0 0 auto"
              >
                {restaurantData ? "Edit" : "Add"} Restaurant
              </DrawerHeader>

              <DrawerBody
                flex="1 1 auto"
                overflowY="auto"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                <VStack spacing={4}>
                  <FormControl
                    isInvalid={!!errors.mainImage && !!touched.mainImage}
                  >
                    <FormLabel>Main Restaurant Image</FormLabel>
                    <Button
                      leftIcon={<FiUpload />}
                      onClick={() =>
                        document.getElementById("main-image-upload")?.click()
                      }
                      variant="ghost"
                      color="#E24441"
                      border="1px dashed #E24441"
                      p={4}
                      borderRadius="md"
                      isDisabled={uploadingImages}
                    >
                      Upload Main Image
                    </Button>
                    <Input
                      id="main-image-upload"
                      type="file"
                      accept="image/*"
                      display="none"
                      onChange={(e) => handleMainImageUpload(e, setFieldValue)}
                    />
                    {mainImage.preview && (
                      <Box
                        mt={2}
                        position="relative"
                        width="100px"
                        height="100px"
                      >
                        <Image
                          src={mainImage.preview}
                          alt="Main Image"
                          boxSize="100px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                        <IconButton
                          aria-label="Remove main image"
                          icon={<RiDeleteBin6Line />}
                          color="#E24441"
                          size="sm"
                          position="absolute"
                          top={-1}
                          right={-1}
                          background="white"
                          onClick={() => removeMainImage(setFieldValue)}
                        />
                      </Box>
                    )}
                    <FormErrorMessage>{errors.mainImage}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.gallery && !!touched.gallery}
                  >
                    <FormLabel>Gallery</FormLabel>
                    <Button
                      leftIcon={<FiUpload />}
                      onClick={() =>
                        document.getElementById("gallery-upload")?.click()
                      }
                      variant="ghost"
                      color="#E24441"
                      border="1px dashed #E24441"
                      p={4}
                      borderRadius="md"
                      isDisabled={uploadingImages}
                    >
                      Upload Images
                    </Button>
                    <Input
                      id="gallery-upload"
                      type="file"
                      multiple
                      accept="image/*"
                      display="none"
                      onChange={(e) =>
                        handleImageUpload(e, "gallery", setFieldValue)
                      }
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      You have to upload atleast 1 image. Supported file types
                      are .png, .jpg, .jpeg
                    </Text>
                    <Grid templateColumns="repeat(5, 1fr)" gap={1} mt={2}>
                      {galleryImages.previews.map((preview, index) => (
                        <GridItem key={index} position="relative">
                          <Image
                            src={preview}
                            alt={`Gallery ${index}`}
                            boxSize="100px"
                            objectFit="cover"
                            borderRadius={"6px"}
                          />
                          <IconButton
                            aria-label="Remove image"
                            color="#E24441"
                            icon={<RiDeleteBin6Line />}
                            size="xsm"
                            position="absolute"
                            background="white"
                            top={2}
                            right={7}
                            onClick={() =>
                              removeImage(index, "gallery", setFieldValue)
                            }
                            padding={"5px"}
                            boxShadow="0px -1.76px 27.68px 0px #b0b0b033"
                          />
                        </GridItem>
                      ))}
                    </Grid>
                    <Text
                      fontSize="sm"
                      color={
                        galleryImages.previews.length < 1
                          ? "red.500"
                          : "gray.500"
                      }
                      mt={1}
                    >
                      {galleryImages.previews.length > 1
                        ? `${galleryImages.previews.length} images are uploaded`
                        : `${galleryImages.previews.length}/1 image uploaded`}
                    </Text>
                    <FormErrorMessage>{errors.gallery}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.menu && !!touched.menu}>
                    <FormLabel>Menu</FormLabel>
                    <Button
                      leftIcon={<FiUpload />}
                      onClick={() =>
                        document.getElementById("menu-upload")?.click()
                      }
                      variant="ghost"
                      color="#E24441"
                      border="1px dashed #E24441"
                      p={4}
                      borderRadius="md"
                      isDisabled={uploadingImages}
                    >
                      Upload Menu & Popular Dishes Images
                    </Button>
                    <Input
                      id="menu-upload"
                      type="file"
                      multiple
                      accept="image/*"
                      display="none"
                      onChange={(e) =>
                        handleImageUpload(e, "menu", setFieldValue)
                      }
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      You have to upload atleast 1 images. Supported file types
                      are .png, .jpg, .jpeg
                    </Text>
                    <Grid templateColumns="repeat(5, 1fr)" gap={2} mt={2}>
                      {menuImages.previews.map((preview, index) => (
                        <GridItem key={index} position="relative">
                          <Image
                            src={preview}
                            alt={`Menu ${index}`}
                            boxSize="100px"
                            objectFit="cover"
                          />
                          <IconButton
                            aria-label="Remove image"
                            color="#E24441"
                            icon={<RiDeleteBin6Line />}
                            size="xsm"
                            position="absolute"
                            background="white"
                            top={2}
                            right={7}
                            onClick={() =>
                              removeImage(index, "menu", setFieldValue)
                            }
                            padding={"5px"}
                            boxShadow="0px -1.76px 27.68px 0px #b0b0b033"
                          />
                        </GridItem>
                      ))}
                    </Grid>
                    <Text
                      fontSize="sm"
                      color={
                        menuImages.previews.length < 1 ? "red.500" : "gray.500"
                      }
                      mt={1}
                    >
                      {menuImages.previews.length > 1
                        ? `${menuImages.previews.length} images are uploaded`
                        : `${menuImages.previews.length}/1 images uploaded`}
                      {/* {menuImages.previews.length}/3 images uploaded */}
                    </Text>
                    <FormErrorMessage>{errors.menu}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.name && !!touched.name}>
                    <FormLabel>Restaurant Name</FormLabel>
                    <Field
                      as={Input}
                      name="name"
                      placeholder="Enter restaurant name"
                    />
                    <FormErrorMessage>{errors.name as string}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.about && !!touched.about}>
                    <FormLabel>About</FormLabel>
                    <Field
                      as={Input}
                      name="about"
                      placeholder="Enter description about restaurant"
                    />
                    <FormErrorMessage>{errors.about}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.street && !!touched.street}>
                    <FormLabel>Street Address</FormLabel>
                    <Field
                      as={Input}
                      name="street"
                      placeholder="Street address"
                    />
                    <FormErrorMessage>
                      {errors.street && String(errors.street)}
                    </FormErrorMessage>
                  </FormControl>

                  <HStack spacing={4} w="full">
                    <FormControl isInvalid={!!errors.city && !!touched.city}>
                      <FormLabel>City</FormLabel>
                      <Field as={Input} name="city" placeholder="City" />
                      <FormErrorMessage>
                        {errors.city && String(errors.city)}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.state && !!touched.state}>
                      <FormLabel>State</FormLabel>
                      <Field as={Input} name="state" placeholder="State" />
                      <FormErrorMessage>
                        {errors.state && String(errors.state)}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.zip && !!touched.zip}>
                      <FormLabel>Post Code</FormLabel>
                      <Field as={Input} name="zip" placeholder="Post code" />
                      <FormErrorMessage>
                        {errors.zip && String(errors.zip)}
                      </FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl
                      isInvalid={!!errors.longitude && !!touched.longitude}
                    >
                      <FormLabel>Longitude</FormLabel>
                      <Field
                        as={Input}
                        name="longitude"
                        type="number"
                        step="any"
                      />
                      <FormErrorMessage>
                        {errors.longitude && String(errors.longitude)}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={!!errors.latitude && !!touched.latitude}
                    >
                      <FormLabel>Latitude</FormLabel>
                      <Field
                        as={Input}
                        name="latitude"
                        type="number"
                        step="any"
                      />
                      <FormErrorMessage>{errors.latitude}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl isInvalid={!!errors.phone && !!touched.phone}>
                      <FormLabel>Phone Number</FormLabel>
                      <Field
                        as={Input}
                        name="phone"
                        placeholder="Enter phone number"
                      />
                      <FormErrorMessage>
                        {errors.phone as string}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.email && !!touched.email}>
                      <FormLabel>Email</FormLabel>
                      <Field
                        as={Input}
                        name="email"
                        placeholder="Enter email"
                        type="email"
                      />
                      <FormErrorMessage>
                        {errors.email as string}
                      </FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl
                      isInvalid={!!errors.openingTime && !!touched.openingTime}
                    >
                      <FormLabel>Restaurant opening time</FormLabel>
                      <Field as={Select} name="openingTime">
                        {Array.from({ length: 24 }).map((_, i) => (
                          <option
                            key={i}
                            value={`${i.toString().padStart(2, "0")}:00`}
                          >
                            {`${i.toString().padStart(2, "0")}:00`}
                          </option>
                        ))}
                      </Field>
                      <FormErrorMessage>
                        {errors.openingTime as string}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={!!errors.closingTime && !!touched.closingTime}
                    >
                      <FormLabel>Restaurant closing time</FormLabel>
                      <Field as={Select} name="closingTime">
                        {Array.from({ length: 24 }).map((_, i) => (
                          <option
                            key={i}
                            value={`${i.toString().padStart(2, "0")}:00`}
                          >
                            {`${i.toString().padStart(2, "0")}:00`}
                          </option>
                        ))}
                      </Field>
                      <FormErrorMessage>
                        {errors.closingTime as string}
                      </FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="full">
                    <FormControl
                      isInvalid={
                        !!errors.bookingFeeLunch && !!touched.bookingFeeLunch
                      }
                    >
                      <FormLabel>Booking fee per person (Lunch)</FormLabel>
                      <Field
                        as={Input}
                        name="bookingFeeLunch"
                        placeholder="Enter lunch booking fee"
                        type="number"
                      />
                      <FormErrorMessage>
                        {errors.bookingFeeLunch as string}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={
                        !!errors.bookingFeeDinner && !!touched.bookingFeeDinner
                      }
                    >
                      <FormLabel>Booking fee per person (Dinner)</FormLabel>
                      <Field
                        as={Input}
                        name="bookingFeeDinner"
                        placeholder="Enter dinner booking fee"
                        type="number"
                      />
                      <FormErrorMessage>
                        {errors.bookingFeeDinner as string}
                      </FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <FormControl
                    isInvalid={!!errors.priceBand && !!touched.priceBand}
                  >
                    <FormLabel>Price Band</FormLabel>
                    <Field as={Select} name="priceBand">
                      {PRICE_BAND_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Field>
                    <FormErrorMessage>{errors.priceBand}</FormErrorMessage>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Terms & Conditions:</FormLabel>
                    <Field
                      as={Select}
                      name="termsAndConditionDropdown"
                      placeholder="Select terms & conditions"
                      value={
                        selectedTemplate
                          ? selectedTemplate._id
                          : values.termsAndCondition
                          ? "custom"
                          : ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const selectedId = e.target.value
                        if (selectedId === "custom" || !selectedId) {
                          setSelectedTemplate(null)
                          setMarkdownContent(restaurantData.termsAndCondition)
                          setFieldValue(
                            "termsAndCondition",
                            restaurantData.termsAndCondition
                          )
                        } else {
                          const found = termsTemplates.find(
                            (t) => t._id === selectedId
                          )
                          setSelectedTemplate(found)
                          const markdown = found?.markdown || ""
                          setMarkdownContent(markdown)
                          setFieldValue("termsAndCondition", markdown)
                        }
                      }}
                    >
                      {termsTemplates.map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.name}
                        </option>
                      ))}
                      <option value="custom" key="custom">
                        Custom Terms & Conditions
                      </option>
                    </Field>
                  </FormControl>
                  <FormEditor
                    // label="Terms & Conditions"
                    required={true}
                    name=""
                    fieldName="termsAndCondition"
                    filedValue={values.termsAndCondition}
                    externalValue={markdownContent}
                  />

                  <MultiSelectWithTags
                    label="Serve Time"
                    options={SERVE_TIME_OPTIONS}
                    selectedValues={values.serveTime}
                    onChange={(newValues: string[]) =>
                      setFieldValue("serveTime", newValues)
                    }
                    errors={errors.serveTime}
                    touched={touched.serveTime}
                  />

                  <MultiSelectWithTags
                    label="Cuisine Type"
                    selectedValues={values.cuisines}
                    apiOptions={cuisineOptions}
                    onChange={(newValues: string[]) =>
                      setFieldValue("cuisines", newValues)
                    }
                    errors={errors.cuisines}
                    touched={touched.cuisines}
                  />

                  <MultiSelectWithTags
                    label="Good for"
                    apiOptions={goodForOptions}
                    selectedValues={values.crowd}
                    onChange={(newValues: string[]) =>
                      setFieldValue("crowd", newValues)
                    }
                    errors={errors.crowd}
                    touched={touched.crowd}
                  />

                  <MultiSelectWithTags
                    label="Features"
                    apiOptions={featureOptions}
                    selectedValues={values.features}
                    onChange={(newValues: string[]) =>
                      setFieldValue("features", newValues)
                    }
                    errors={errors.features}
                    touched={touched.features}
                  />
                  <MultiSelectWithTags
                    label="Dishes"
                    apiOptions={dishesOptions}
                    selectedValues={values.popularDishes}
                    onChange={(newValues: string[]) =>
                      setFieldValue("popularDishes", newValues)
                    }
                    onAddNew={handleAddNewDish}
                    errors={errors.popularDishes}
                    touched={touched.popularDishes}
                  />

                  <FormControl width={"50%"} alignSelf="flex-start">
                    <FormLabel>Restaurant Rating</FormLabel>
                    <Tooltip
                      title={`${values.rating.toFixed(1)} / 5 Stars`}
                      placement="top"
                    >
                      <Slider
                        min={1}
                        max={5}
                        step={0.1}
                        value={values.rating}
                        onChange={(value) => setFieldValue("rating", value)}
                        marks={{
                          1: "1",
                          2: "2",
                          3: "3",
                          4: "4",
                          5: "5",
                        }}
                        {...customSlideStyles}
                      />
                    </Tooltip>
                    <Flex justifyContent="flex-start" mt={2}>
                      <Badge
                        bg="#FFF1F1"
                        color="#E24441"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm"
                        display="inline-flex"
                        alignItems="center"
                        variant="solid"
                        fontWeight={"400"}
                      >
                        Your Rating: {values.rating.toFixed(1)} / 5 Stars
                      </Badge>
                    </Flex>
                  </FormControl>

                  <FormControl width={"50%"} alignSelf="flex-start">
                    <FormLabel mb={2}>Does Include Drinks</FormLabel>
                    <Switch
                      checked={values.doesIncludeDrinks}
                      onChange={(checked) =>
                        setFieldValue(
                          "doesIncludeDrinks",
                          !values.doesIncludeDrinks
                        )
                      }
                      style={{
                        backgroundColor: values.doesIncludeDrinks
                          ? "#E24441"
                          : "",
                      }}
                    />

                    <Flex justifyContent="flex-start" mt={2}>
                      <Badge
                        bg={values.doesIncludeDrinks ? "#E6FFFA" : "#FFF5F5"}
                        color={values.doesIncludeDrinks ? "#319795" : "#E53E3E"}
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm"
                        display="inline-flex"
                        alignItems="center"
                        variant="solid"
                        fontWeight={"400"}
                      >
                        {values.doesIncludeDrinks
                          ? "Drinks Included"
                          : "Drinks Not Included"}
                      </Badge>
                    </Flex>
                  </FormControl>

                  <FormControl width={"50%"} alignSelf="flex-start">
                    <FormLabel mb={2}>Is Sponsored</FormLabel>
                    <Switch
                      checked={values.isSponsored}
                      onChange={(checked) =>
                        setFieldValue("isSponsored", !values.isSponsored)
                      }
                      style={{
                        backgroundColor: values.isSponsored ? "#E24441" : "",
                      }}
                    />

                    <Flex justifyContent="flex-start" mt={2}>
                      <Badge
                        bg={values.isSponsored ? "#E6FFFA" : "#FFF5F5"}
                        color={values.isSponsored ? "#319795" : "#E53E3E"}
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm"
                        display="inline-flex"
                        alignItems="center"
                        variant="solid"
                        fontWeight={"400"}
                      >
                        {values.isSponsored ? "Sponsored" : "Not Sponsored"}
                      </Badge>
                    </Flex>
                  </FormControl>
                </VStack>
              </DrawerBody>

              <DrawerFooter
                borderTopWidth="1px"
                flex="0 0 auto"
                bg="white"
                position="sticky"
                bottom={0}
                zIndex={1}
              >
                <Button
                  variant="outline"
                  mr={3}
                  border="1px solid #EEE"
                  borderRadius="6px"
                  color="#424242"
                  fontSize={"sm"}
                  onClick={onClose}
                  type="button"
                  isDisabled={uploadingImages}
                >
                  Cancel
                </Button>
                <Button
                  background="#E24441"
                  fontSize={"sm"}
                  color="#fff"
                  type="submit"
                  _hover={{ background: "#E24441" }}
                  // isLoading={isSubmitting}
                  isDisabled={uploadingImages}
                  isLoading={uploadingImages || isSubmitting}
                >
                  {restaurantData ? "Update" : "Add"} Restaurant
                </Button>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
        <DishesEditModal
          isOpen={isAddDishModalOpen}
          onClose={handleCloseDishModal}
          onDishAdded={handleDishAdded}
          dishData={selectedDish}
        />
      </DrawerContent>
    </Drawer>
  )
}

export default RestaurantEditDrawer
